import { Observable } from 'rxjs';
import { FormControl } from '@angular/forms';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { GoogleMapService } from 'src/app/modules/shared/services/google.map.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'ig-address-input',
  templateUrl: './address.input.component.html',
  styleUrls: ['./address.input.component.scss'],
})
export class AddressInputComponent implements AfterViewInit, OnDestroy {
  googleLoaded$: Observable<boolean>;
  placeAutocompleteElement: google.maps.places.PlaceAutocompleteElement;
  private valueChangesSubscription: Subscription;
  placeListener: google.maps.MapsEventListener;
  isUsingNewElement = false;
  private selectingPlace = false;
  placeholderEl: HTMLElement;

  @Input() label: string = 'Location / Address* (or check Virtual Events)';
  @Input() hint: string =
    'Start typing the physical address and select from a verified Google Maps address below';
  @Input() control: FormControl;
  @Input() cls: string = '';
  @Input() isReadOnly: boolean = false;
  @Output() addressSelected: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('autocompleteContainer', { static: true })
  autocompleteContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('inputElement', { static: true })
  inputElement!: any;

  constructor(public googleMapService: GoogleMapService, private ngZone: NgZone) {
    try {
      this.initLabelOfInput();
      this.googleLoaded$ = this.googleMapService.loadGoogleMapApi();
      applyGmpBorderPatchOnce();
    } catch (e) {
      console.error(e);
    }
  }

  ngAfterViewInit() {
    this.setInitialDisplayValue();

    this.valueChangesSubscription = this.control.valueChanges.subscribe((value) => {
      this.updateDisplayValue(value);
    });

    try {
      this.googleLoaded$.subscribe((isLoaded: boolean) => {
        if (!isLoaded) return;

        // Check if we can use the new PlaceAutocompleteElement
        this.initializeAutocomplete();
      });
    } catch (e) {
      console.error(e);
    }
  }

  ngOnDestroy() {
    if (this.placeListener) {
      google.maps.event.removeListener(this.placeListener);
    }

    if (this.placeAutocompleteElement && this.placeAutocompleteElement.parentNode) {
      this.placeAutocompleteElement.parentNode.removeChild(this.placeAutocompleteElement);
    }

    if (this.valueChangesSubscription) {
      this.valueChangesSubscription.unsubscribe();
    }
  }

  async initializeAutocomplete() {
    try {
      // Try to use the new PlaceAutocompleteElement
      const placesLibrary = this.googleMapService.getPlacesLibrary();

      if (placesLibrary && google.maps.places.PlaceAutocompleteElement) {
        this.initializeNewAutocomplete();
      } else {
        // Fallback to old method if new element is not available
        this.initializeLegacyAutocomplete();
      }
    } catch (error) {
      console.error('Error initializing autocomplete:', error);
      // Fallback to legacy method
      this.initializeLegacyAutocomplete();
    }
  }

  setPlaceHolderStyle() {
    if (!this.placeholderEl) return;
    if (this.control.value) {
      this.placeholderEl.style.color = '#676767';
      this.placeholderEl.style.fontSize = '11px';
      this.placeholderEl.style.top = '-9px';
      this.placeholderEl.style.left = '10px';
      this.placeholderEl.style.padding = '0 8px';
      this.placeholderEl.style.backgroundColor = 'white';
    } else {
      this.placeholderEl.style.color = '#9e9e9e';
      this.placeholderEl.style.fontSize = '16px';
      this.placeholderEl.style.top = '16px';
      this.placeholderEl.style.left = '16px';
    }
  }

  initializeNewAutocomplete() {
    const el = document.createElement('gmp-place-autocomplete') as any;

    el.setAttribute('fields', 'formattedAddress,location,addressComponents,id');
    const initialValue = this.control?.value;
    const initialDisplayValue = typeof initialValue === 'object' && initialValue?.address ? initialValue.address : (initialValue || '');
    el.setAttribute('value', initialDisplayValue);
    this.autocompleteContainer.nativeElement.appendChild(el);
    this.isUsingNewElement = true;

    // Set country restriction using the property (not attribute)
    el.includedRegionCodes = ['us', 'pt', 'uk', 'ca'];

    const placeholderEl = document.createElement('div');
    placeholderEl.textContent = this.label;
    placeholderEl.style.position = 'absolute';
    placeholderEl.style.top = '16px';
    placeholderEl.style.left = '16px';
    placeholderEl.style.color = '#9e9e9e';
    placeholderEl.style.fontSize = '16px';
    placeholderEl.style.fontFamily = 'inherit';
    placeholderEl.style.pointerEvents = 'none';
    placeholderEl.style.transition = 'all 0.15s ease';
    this.placeholderEl = placeholderEl;

    this.autocompleteContainer.nativeElement.appendChild(placeholderEl);

    // Set initial value if it exists
    const setInitialValue = () => {
      // Also try to find the internal input and set its value
      const shadowRoot = el.shadowRoot;
      if (shadowRoot) {
        const input = shadowRoot.querySelector('input');
        if (input) {
          const controlValue = this.control.value;
          input.value = typeof controlValue === 'object' && controlValue?.address ? controlValue.address : (controlValue || '');

          input.style.setProperty('font-size', '15px', 'important');
          input.style.setProperty('-webkit-text-size-adjust', '150%', 'important');

          Object.assign(input.style, {
            height: '100%',
            border: 'none',
            outline: 'none',
            background: 'transparent',
            position: 'relative',
            top: '-4px',
            color: 'black',
          });

          if (input.value) {
            placeholderEl.style.color = '#676767';
            placeholderEl.style.fontSize = '11px';
            placeholderEl.style.top = '-9px';
            placeholderEl.style.left = '10px';
            placeholderEl.style.padding = '0 8px';
            placeholderEl.style.backgroundColor = 'white';
          } else {
            placeholderEl.style.color = '#6a6a6a';
          }
        }

        shadowRoot.querySelector('.autocomplete-icon')?.remove();
        shadowRoot.querySelector('.clear-button')?.remove();
        if (input) {
          attachSpaceBarFix(input);
          input.addEventListener('input', () => {
            this.ngZone.run(() => {
              const currentText = input.value?.trim();
              if (typeof this.control.value === 'object' && this.control.value !== null) {
                if (this.control.value.address !== currentText) {
                  this.control.setValue(currentText || null);
                  this.control.markAsDirty();
                }
              }
            });
          });
        }
      }

      if (this.control?.value) {
        try {
          // Set the value attribute
          const val = this.control.value;
          el.setAttribute('value', typeof val === 'object' && val?.address ? val.address : val);
        } catch (e) {
          console.error('Error setting initial value:', e);
        }
      }
    };

    // Try to set the value after a short delay to ensure the element is ready
    setTimeout(setInitialValue, 100);

    // Also listen for the element's load event
    el.addEventListener('load', setInitialValue);

    Object.assign(el.style, {
      display: 'flex',
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #b3b3b3',
      borderRadius: '4px',
      fontFamily: 'inherit',
      outline: 'none',
      boxSizing: 'border-box',
      height: '3.5rem',
      webkitTextSizeAdjust: '150%',
      backgroundColor: 'transparent',
    });

    // Focus/blur styling
    el.addEventListener('focus', () => {
      el.style.border = '2px solid #673ab7';
      el.style.fontSize = '16px';
      el.style.webkitTextSizeAdjust = '150%';
      placeholderEl.style.color = '#673ab7';
      placeholderEl.style.fontSize = '11px';
      placeholderEl.style.top = '-9px';
      placeholderEl.style.left = '10px';
      placeholderEl.style.padding = '0 8px';
      placeholderEl.style.backgroundColor = 'white';
      const input = el.shadowRoot?.querySelector('input');
      if (!input?.value) {
        el.style.border = '2px solid #673ab7';
        placeholderEl.style.color = '#673ab7';
      }
    });
    el.addEventListener('blur', () => {
      el.style.webkitTextSizeAdjust = '150%';
      const input = el.shadowRoot?.querySelector('input');
      const inputValue = input?.value?.trim();

      setTimeout(() => {
        this.ngZone.run(() => {
          this.control.markAsTouched();

          if (this.selectingPlace) return;
          if (typeof this.control.value === 'object' && this.control.value !== null && this.control.value.address) return;

          if (inputValue && (!this.control.value || typeof this.control.value === 'string')) {
            this.control.setValue(inputValue);
            this.control.setErrors({ invalidAddress: true });
            this.control.updateValueAndValidity();
          }
        });

        if (!inputValue || inputValue === '') {
          placeholderEl.style.display = 'block';
          if (this.control.invalid && this.control.touched) {
            placeholderEl.style.color = '#f44336';
            el.style.border = '1px solid #f44336';
          } else {
            placeholderEl.style.color = '#9e9e9e';
            el.style.border = '1px solid #b3b3b3';
          }
          placeholderEl.style.top = '15px';
          placeholderEl.style.left = '9px';
          placeholderEl.style.fontSize = '15px';
        } else {
          if (this.control.invalid && this.control.touched) {
            el.style.border = '1px solid #f44336';
          } else {
            el.style.border = '1px solid #b3b3b3';
          }
          placeholderEl.style.top = '-9px';
          placeholderEl.style.left = '10px';
          placeholderEl.style.color = '#9e9e9e';
          placeholderEl.style.padding = '0 8px';
        }
      }, 150);
    });

    el.addEventListener('gmp-select', async ({ placePrediction }: any) => {
      this.selectingPlace = true;
      const predictionText: string = placePrediction?.text?.text ?? placePrediction?.text ?? '';
      try {
        const place = await placePrediction.toPlace();
        await place.fetchFields({
          fields: ['formattedAddress', 'location', 'addressComponents', 'id'],
        });
        this.ngZone.run(() => {
          this.selectingPlace = false;
          this.handlePlaceSelection(place, predictionText);
        });
      } catch (err) {
        this.selectingPlace = false;
        console.error('Failed to load place details', err);
      }
    });

    this.placeAutocompleteElement = el as google.maps.places.PlaceAutocompleteElement;
  }

  initializeLegacyAutocomplete() {
    // Fallback to the old Autocomplete method
    const input = this.inputElement.input.nativeElement as HTMLInputElement;

    const autoComplete = new google.maps.places.Autocomplete(input, {
      fields: ['address_components', 'geometry', 'formatted_address', 'name', 'place_id'],
      componentRestrictions: { country: ['us', 'pt', 'uk', 'ca'] },
    });

    // Prevent zoom-in on iOS when input is focused
    input.style.fontSize = '16px';

    // iOS specific fixes
    this.applyIOSFixes(input);
    input.addEventListener('input', () => {
      this.ngZone.run(() => {
        const currentText = input.value?.trim();
        if (typeof this.control.value === 'object' && this.control.value !== null) {
          if (this.control.value.address !== currentText) {
            this.control.setValue(currentText || null);
            this.control.markAsDirty();
          }
        }
      });
    });

    input.addEventListener('focus', () => {
    });

    input.addEventListener('blur', () => {
      this.ngZone.run(() => {
        const inputValue = input.value?.trim();
        this.control.markAsTouched();

        if (inputValue && (
          !this.control.value ||
          typeof this.control.value === 'string' ||
          (typeof this.control.value === 'object' && this.control.value.address !== inputValue)
        )) {
          this.control.setValue(inputValue);
          this.control.setErrors({ invalidAddress: true });
          this.control.updateValueAndValidity();
        }
      });
    });

    // Add place_changed event listener
    this.placeListener = autoComplete.addListener('place_changed', () => {
      this.ngZone.run(() => this.handlePlaceSelection(autoComplete.getPlace()));
    });

    console.log('Using legacy Autocomplete');
  }

  applyIOSFixes(input: HTMLInputElement) {
    // Always make the field editable
    const unlock = () => input.removeAttribute('readonly');

    // Google adds readonly on 'focus', so undo it right away
    input.addEventListener('focus', unlock, true);
    // Some Android devices only fire touchstart/end before focus
    input.addEventListener('touchstart', unlock, true);

    // Monitor DOM for pac-container and apply fixes when it appears
    this.addPacContainerFix();
  }

  addPacContainerFix() {
    // Create a mutation observer to detect when Google adds the pac-container to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          // Look for pac-container in the document
          const pacContainer = document.querySelector('.pac-container');
          if (pacContainer) {
            // Add z-index to ensure it's visible
            (pacContainer as HTMLElement).style.zIndex = '1000';

            // Prevent touchend events from bubbling up
            pacContainer.addEventListener(
              'touchend',
              (e) => {
                e.stopImmediatePropagation();
              },
              true
            );

            // Apply needsclick to all pac-items to prevent fastclick issues
            const pacItems = pacContainer.querySelectorAll('.pac-item, .pac-item span');
            pacItems.forEach((item) => {
              item.classList.add('needsclick');
            });

            // Once we've found and fixed the pac-container, disconnect the observer
            observer.disconnect();
          }
        }
      });
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  handlePlaceSelection(place: any, predictionText?: string) {
    if (!place?.formattedAddress) {
      console.warn('No place details', place);
      return;
    }

    const rawComps: any[] = place.addressComponents ?? place['address_components'] ?? [];
    const comps = rawComps.map((c: any) => ({
      ...c,
      short_name: c.short_name ?? c.shortText ?? '',
      long_name: c.long_name ?? c.longText ?? '',
      shortText: c.shortText ?? c.short_name ?? '',
      longText: c.longText ?? c.long_name ?? '',
      types: Array.isArray(c.types) ? c.types : [],
    }));
    const get = (t: string, short = false) =>
      comps.find((c) => c.types?.includes(t))?.[short ? 'shortText' : 'longText'] ?? '';

    const hasStreetNumber = comps.some((c) => c.types?.includes('street_number'));
    const finalAddress = (!hasStreetNumber && predictionText) ? predictionText : place.formattedAddress;
    const rawZip = (get('postal_code', true) || get('postal_code') || '').trim();
    const usZip = rawZip.match(/(\d{5})/);
    const zipCode = usZip ? usZip[1] : rawZip;

    const placeData = {
      lat: place.location?.lat() ?? null,
      lng: place.location?.lng() ?? null,
      address: finalAddress,
      address_components: comps,
      placeId: place.id,
      cityName: get('locality'),
      stateAbbr: get('administrative_area_level_1', true),
      country: get('country'),
      zipCode,
    };

    this.control.setValue(placeData);
    this.control.updateValueAndValidity();
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.addressSelected.emit(placeData);

    /* keep the UI in sync */
    (this.placeAutocompleteElement as any).value = finalAddress;
    (this.placeAutocompleteElement as any).style.border = '1px solid #b3b3b3';
  }

  clear() {
    this.control.setValue(null);

    if (this.isUsingNewElement) {
      (this.placeAutocompleteElement as any).value = '';
    } else {
      this.inputElement.input.nativeElement.value = '';
    }
  }
  hasTextButNoSelection(): boolean {
    let inputValue = '';
    
    if (this.isUsingNewElement) {
      const input = this.placeAutocompleteElement?.shadowRoot?.querySelector('input');
      inputValue = input?.value?.trim() || '';
    } else {
      const input = this.inputElement?.input?.nativeElement as HTMLInputElement;
      inputValue = input?.value?.trim() || '';
    }

    if (!inputValue) return false;
    if (!this.control.value) return true;
    if (typeof this.control.value === 'string') return true;
    if (typeof this.control.value === 'object' && this.control.value.address !== inputValue) return true;

    return false;
  }

  private setInitialDisplayValue() {
    const currentValue = this.control.value;
    this.updateDisplayValue(currentValue);
  }

  private updateDisplayValue(value: any) {
    const el = document.querySelector('gmp-place-autocomplete');
    const shadowRoot = el?.shadowRoot;

    if (value) {
      let displayValue = '';

      if (typeof value === 'object' && value.address) {
        displayValue = value.address;
      } else if (typeof value === 'string') {
        displayValue = value;
      }

      if (shadowRoot) {
        const input = shadowRoot.querySelector('input');
        if (input) {
          input.value = displayValue;
        }
      }
    } else {
      if (shadowRoot) {
        const input = shadowRoot.querySelector('input');
        if (input) {
          input.value = '';
        }
      }
    }
    this.setPlaceHolderStyle();
  }

  initLabelOfInput() {
    const currentUrl = window.location.href;

    if (currentUrl.includes('/events')) {
      this.label = 'Location / Address* (or check Virtual Events)';
    } else {
      this.label = 'Location / Address*';
    }
  }
}

function attachSpaceBarFix(input: HTMLInputElement): void {
  if ((input as any).__imgoingSpaceBarFixAttached) return;
  (input as any).__imgoingSpaceBarFixAttached = true;

  let valueBeforeKeyDown = '';
  let cursorBeforeKeyDown = 0;

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.code === 'Space') {
      valueBeforeKeyDown = input.value;
      cursorBeforeKeyDown = input.selectionStart ?? 0;
    }
  };

  const onKeyUp = (e: KeyboardEvent) => {
    if (e.key === ' ' || e.code === 'Space') {
      if (input.value === valueBeforeKeyDown) {
        const expectedValue =
          valueBeforeKeyDown.slice(0, cursorBeforeKeyDown) + ' ' + valueBeforeKeyDown.slice(cursorBeforeKeyDown);
        input.value = expectedValue;
        input.setSelectionRange(cursorBeforeKeyDown + 1, cursorBeforeKeyDown + 1);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
  };

  input.addEventListener('keydown', onKeyDown, true);
  input.addEventListener('keyup', onKeyUp, true);
}

function applyGmpBorderPatchOnce(): void {
  // Guard so we don't patch more than once
  if ((window as any).__gmpBorderPatchApplied) {
    return;
  }
  (window as any).__gmpBorderPatchApplied = true;

  const originalAttach = Element.prototype.attachShadow;

  Element.prototype.attachShadow = function (init: ShadowRootInit) {
    // Intercept only <gmp-place-autocomplete>
    if (this.localName === 'gmp-place-autocomplete') {
      // force mode:"open" so we can style inside
      const shadow = originalAttach.call(this, { ...init, mode: 'open' });

      // Inject <style> that kills the inner border
      const style = document.createElement('style');
      style.textContent = `
        /* hides the nested border that causes the "double border" */
        .widget-container,
        .widget-container:focus-within,
        .input-container,
        .input-container:focus-within {
          border: none !important;
        }

        /* suppress native blue focus ring on the input itself */
        input {
          outline: none !important;
          box-shadow: none !important;
          border: none !important;
          font-size: 11px !important;
          font-family: inherit !important;
          -webkit-text-size-adjust: 150%;
          pointer-events: auto !important;
        }

        .focus-ring {
          outline: none !important;
          box-shadow: none !important;
          border: none !important;
          font-size: 16px !important; /* Minimum 16px to prevent iOS zoom */
        }

        .input-container {
          padding: 8px 0 !important;
          font-size: 16px !important; /* Minimum 16px to prevent iOS zoom */
        }

        /* Additional iOS zoom prevention */
        input[type="text"],
        input[type="search"] {
          -webkit-text-size-adjust: 150%;
          font-size: 16px !important;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
        }
      `;
      shadow.appendChild(style);

      return shadow; // expose the patched shadow root
    }

    // All other elements keep their default behaviour
    return originalAttach.call(this, init);
  };
}
